import { AfterViewInit, Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { inject } from '@angular/core';
import { AuthService } from '../../services/auth.service';
import { ActivatedRoute } from '@angular/router';

// Components.
import { MfaDialogComponent } from './mfa-dialog/mfa-dialog.component';

// Translator.
import { TranslateService, TranslatePipe } from '@ngx-translate/core';

// Material.
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { NgxSpinnerModule, NgxSpinnerService } from 'ngx-spinner';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { firstValueFrom } from 'rxjs';

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrl: './login.component.scss',
  standalone: true,
  imports: [
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatCardModule,
    MatButtonModule,
    ReactiveFormsModule,
    RouterModule,
    TranslatePipe,
    MatDialogModule,
    NgxSpinnerModule,
    MatSnackBarModule
  ]
})

export class LoginComponent implements OnInit {
  constructor(
    public translate: TranslateService,
    private dialog: MatDialog,
    private spinner: NgxSpinnerService,
    private route: ActivatedRoute
  ) {
    if(this.authService.isLoggedIn()){
      this.router.navigate(['/dashboard']);
    }
  }
  private _snackBar = inject(MatSnackBar);

  authService = inject(AuthService);
  router = inject(Router);
  hidePassword: boolean = true;

  private animationFrameId!: number;

  public loginForm = new FormGroup({
    employeeNumber: new FormControl('', [Validators.required]),
    password: new FormControl('', [Validators.required])
  })

  async ngOnInit() {
    this.route.queryParams.subscribe(async (params) => {
      const code = params['code'];
      if (code) {
        this.spinner.show();

        try {
          const mfaResponse = await this.authService.verifyCode(code);

          const validationResponse = await firstValueFrom(this.authService.ValidateToken(mfaResponse.idtoken));

          if (validationResponse.success === 1) {
            const userName = validationResponse.data || 'User';
            this.spinner.hide();
            this.authService.saveUserSession(mfaResponse.idtoken, userName);
            this.router.navigate(['/dashboard']);
          } else {
            this.openSnackBar(validationResponse.message);
          }
        } 
        catch (error) {
          this.spinner.hide();
        
          let statusCode = 'Unknown';
          let errorMessage = 'An error occurred trying to connect to web services.';
        
          if (error instanceof Error) {

            errorMessage = error.message;
          } else if (typeof error === 'object' && error !== null && 'status' in error) {
            statusCode = (error as any).status;
            errorMessage = (error as any)?.error?.message || errorMessage;
          }
        
          this.openSnackBar(`Error ${statusCode}: ${errorMessage}`);
          console.error(`Error ${statusCode}`, error);
        }
        
      }
    });
  }

  ngOnDestroy(): void {
    cancelAnimationFrame(this.animationFrameId);
  }

  async onSubmit() {
    this.spinner.show();
  
    if (this.loginForm.valid) {
      const userName = this.loginForm.get('employeeNumber')?.value!;
      const password = this.loginForm.get('password')?.value!;
  
      try {
        const response: any = await this.authService.login(userName, password);
  
        if (response.session) {
          const dialogRef = this.dialog.open(MfaDialogComponent);
          const mfaCode = await dialogRef.afterClosed().toPromise();
  
          if (!mfaCode) {
            this.spinner.hide();
            this.openSnackBar("MFA authentication was cancelled");
            return;
          }
  
          const mfaResponse = await this.authService.verifyMfa(userName, response.session, mfaCode);

          this.authService.ValidateToken(mfaResponse.idtoken).subscribe({
            next: (response) => {
              if (response.success === 1) {
                this.authService.saveUserSession(mfaResponse.idtoken, userName);
                this.router.navigate(['/dashboard']);
                
              } else {
                this.openSnackBar(response.message);
              }
            },
            error: (error) => {
    
              this.spinner.hide();
              const statusCode = error.status;
              const errorMessage = error.error?.message || "An error occurred trying to connect to web services.";
        
              this.openSnackBar(`Error ${statusCode}: ${errorMessage}`);
              console.error(`Error ${statusCode}`, error);
            },
          });
        } else if (response.idtoken) {
          this.authService.ValidateToken(response.idtoken);
          this.authService.saveUserSession(response.idtoken, userName);
          this.router.navigate(['/dashboard']);
          this.openSnackBar("Login successful!");
  
        } else {
          this.spinner.hide();
          this.openSnackBar("Unexpected error during login");
        }
  
      } catch (error) {
        this.spinner.hide();
        this.openSnackBar("Invalid credentials or authentication error");
        console.error(error);
  
      } finally {

      }
    } else {
      this.spinner.hide();
      this.openSnackBar("Please enter valid credentials");
    }
  }
  

  openSnackBar(Message: string) {
    this._snackBar.open(Message, "Dismiss", { duration: 5000 });
  }
}
